import { classNames, h } from "omi";

import { addToClipboard } from "@/utils/clipboard.ts";
import { NWAvatar, NWIcon, NWTooltip } from "@/components";
import { Store } from "@/store";

type Props = {
  size: "xs" | "sm" | "md";
  avatarUrls: string[];
  avatarCount?: number;
  completionCount: number;
  withInviteLink?: boolean;
};

export function AvatarStack({ avatarUrls, avatarCount = 3, completionCount, size, withInviteLink }: Props) {
  const hasCompletions = completionCount > 0;

  const createAvatarArray = () => {
    const shouldFillWithEmpty = avatarUrls.length < avatarCount && avatarUrls.length < completionCount;
    const avatars = avatarUrls.slice(0, avatarCount);

    if (shouldFillWithEmpty) {
      const emptyAvatars = Array.from({ length: Math.min(avatarCount, completionCount) - avatarUrls.length }).map(
        () => "",
      );
      return [...avatars, ...emptyAvatars];
    }

    return avatars;
  };

  const copyInviteLink = async () => {
    const projectId = Store.project.id.value;
    if (!projectId) return;

    const inviteLink = `https://link.nextwork.org/projects/${projectId}?utm_source=app&utm_medium=project&utm_campaign=referral`;
    await addToClipboard(inviteLink);

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackInviteLinkCopied("project-completions");
    });
  };

  const avatars = createAvatarArray();

  return (
    <div className={"flex items-center flex-wrap"}>
      {hasCompletions && (
        <>
          <div className={classNames("flex -space-x-2", { hidden: avatars.length === 0 })}>
            {avatars.map((url, index) => (
              <NWAvatar
                key={index}
                imgUrl={url}
                size={size}
                withIndicator={false}
                withProBorder={false}
                interactive={false}
              />
            ))}

            {withInviteLink && (
              <div className="!mx-1">
                <NWTooltip placement="top" content="Copied! Send to a friend " trigger="click" showDuration={2000}>
                  <NWTooltip content="Copy invite link for a friend" placement="top">
                    <button
                      className="transition-all h-9 w-9 border rounded-full border-dashed shrink-0 bg-white hover:bg-gray-50/75 active:bg-gray-50 active:border-gray-300 justify-center items-center inline-flex overflow-hidden"
                      onClick={copyInviteLink}
                    >
                      <NWIcon name="plus" width={16} height={16} className="text-tertiary"></NWIcon>
                    </button>
                  </NWTooltip>
                </NWTooltip>
              </div>
            )}
          </div>

          <span className="ml-2 my-1 text-sm text-tertiary">{formatCount(completionCount)} completed</span>
        </>
      )}
    </div>
  );
}

function formatCount(count: number): string {
  if (count < 10) {
    return `${count}+`;
  }

  const flooredCount = Math.floor(count / 10) * 10;

  if (flooredCount >= 1000000) {
    return `${(flooredCount / 1000000).toLocaleString()}M+`;
  }

  if (flooredCount >= 1000) {
    return `${(flooredCount / 1000).toLocaleString()}K+`;
  }

  return `${flooredCount.toLocaleString()}+`;
}
